"use client"

import { Link } from '@inertiajs/react'
import { ChevronsUpDown, Ghost, PlusCircle } from 'lucide-react'
import { cn } from '@/lib/utils'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/shadcn/dropdown-menu'
import {
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
} from '@/components/ui/shadcn/sidebar'
import type { Account } from '@/types/shared'

interface AccountSwitcherProps {
  currentAccount: Account | null
  accounts: Account[]
}

export function AccountSwitcher({ currentAccount, accounts }: AccountSwitcherProps) {
  const { isMobile, state } = useSidebar()
  const isCollapsed = state === "collapsed"

  return (
    <SidebarMenu>
      <SidebarMenuItem>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <SidebarMenuButton
              size="lg"
              className="data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground cursor-pointer"
            >
              <div className={cn(
                "flex aspect-square size-8 items-center justify-center rounded-lg overflow-hidden",
                !currentAccount?.logo_url && "bg-primary text-primary-foreground",
                isCollapsed && "mx-auto my-2"
              )}>
                {currentAccount?.logo_url ? (
                  <img
                    src={currentAccount.logo_url}
                    alt={currentAccount.name}
                    className="size-full object-cover"
                  />
                ) : (
                  <Ghost className="size-4" />
                )}
              </div>
              {!isCollapsed && (
                <>
                  <div className="grid flex-1 text-left text-sm leading-tight">
                    {currentAccount ? (
                      <>
                        <span className="truncate font-semibold">{currentAccount.name}</span>
                        <span className="truncate text-xs text-muted-foreground">Workspace</span>
                      </>
                    ) : (
                      <span className="truncate font-medium text-muted-foreground">No workspace</span>
                    )}
                  </div>
                  <ChevronsUpDown className="ml-auto size-4" />
                </>
              )}
            </SidebarMenuButton>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            className="w-[--radix-dropdown-menu-trigger-width] min-w-56 rounded-lg"
            align="start"
            side={isMobile ? 'bottom' : 'right'}
            sideOffset={4}
          >
            <DropdownMenuLabel className="text-muted-foreground text-xs">
              Workspaces
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            {accounts.length > 0 ? (
              accounts.map((account) => {
                const isSelected = currentAccount?.id === account.id
                return (
                  <DropdownMenuItem
                    key={account.id}
                    asChild
                    className={cn(
                      'gap-2 p-2 cursor-pointer my-1 rounded-md',
                      'hover:bg-accent hover:text-accent-foreground',
                      '[&_svg]:hover:text-accent-foreground',
                      isSelected && 'bg-accent text-accent-foreground [&_svg]:text-accent-foreground'
                    )}
                  >
                    <Link href={`/${account.slug}/inbox`}>
                      <div className="size-6 rounded flex items-center justify-center overflow-hidden">
                        {account.logo_url ? (
                          <img
                            src={account.logo_url}
                            alt={account.name}
                            className="size-full object-cover"
                          />
                        ) : (
                          <div className={cn(
                            "size-full flex items-center justify-center",
                            isSelected ? "bg-accent-foreground/20" : "bg-primary/20"
                          )}>
                            <Ghost className={cn(
                              "size-3",
                              isSelected ? "text-accent-foreground" : "text-primary"
                            )} />
                          </div>
                        )}
                      </div>
                      <span>{account.name}</span>
                    </Link>
                  </DropdownMenuItem>
                )
              })
            ) : (
              <DropdownMenuItem disabled className="p-2 text-muted-foreground">
                No workspaces available
              </DropdownMenuItem>
            )}
            <DropdownMenuSeparator />
            <DropdownMenuItem
              asChild
              className="gap-2 p-2 cursor-pointer"
            >
              <Link href="/accounts/new">
                <PlusCircle className="size-4" />
                <span>Create Workspace</span>
              </Link>
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </SidebarMenuItem>
    </SidebarMenu>
  )
}
